'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["disabled", "onEdit", "onSelect", "operator", "text", "value"];
import * as InputTypes from 'customer-data-filters/filterQueryFormat/InputTypes';
import * as Operators from 'customer-data-filters/filterQueryFormat/operator/Operators';
import { EQUATION, ROLLUP } from 'customer-data-objects/property/PropertyFieldTypes';
import { DATE } from 'customer-data-objects/property/PropertyTypes';
import { OrderedSet } from 'immutable';
import { isOperatorIncludesObjectsWithNoValueSet, shouldOperatorAlwaysShowNoValueCheckbox } from '../../utilities/defaultNullValue';
import { swapOperator } from 'customer-data-filters/filterQueryFormat/operator/Operator';
import DefaultNullValueRecord from '../../filterQueryFormat/DefaultNullValueRecord';
import FilterOperatorCtaInput from 'customer-data-filters/components/operator/FilterOperatorCtaInput';
import FilterOperatorEmailLinkInput from './FilterOperatorEmailLinkInput';
import FilterOperatorErrorType from 'customer-data-filters/components/propTypes/FilterOperatorErrorType';
import FilterOperatorHighValueInput from './FilterOperatorHighValueInput';
import FilterOperatorIncludeObjectsWithNoAssociatedObjectsInput from './FilterOperatorIncludeObjectsWithNoAssociatedObjectsInput';
import FilterOperatorIncludeObjectsWithNoValueSetInput from './FilterOperatorIncludeObjectsWithNoValueSetInput';
import FilterOperatorNoInputInput from './FilterOperatorNoInputInput';
import FilterOperatorPropertyInput from './FilterOperatorPropertyInput';
import FilterOperatorRollingDateInput from './FilterOperatorRollingDateInput';
import FilterOperatorRollingInequalityInput from './FilterOperatorRollingInequalityInput';
import FilterOperatorRollingPropertyUpdated from './FilterOperatorRollingPropertyUpdated';
import FilterOperatorRollingWithUnitInput from './FilterOperatorRollingWithUnitInput';
import FilterOperatorType from 'customer-data-filters/components/propTypes/FilterOperatorType';
import FilterOperatorUrlInput from './FilterOperatorUrlInput';
import FilterOperatorValueInput from './FilterOperatorValueInput';
import FilterType from '../propTypes/FilterType';
import FormattedMessage from 'I18n/components/FormattedMessage';
import NullRelatedState from '../propTypes/NullRelatedState';
import PropTypes from 'prop-types';
import { Fragment, PureComponent } from 'react';
import { syntheticEvent as SyntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import UIList from 'UIComponents/list/UIList';
import UIRadioInput from 'UIComponents/input/UIRadioInput';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import getIn from 'transmute/getIn';
import getPropertyType from 'customer-data-objects/property/getPropertyType';
import memoize from 'transmute/memoize';
import partial from 'transmute/partial';
import uniqueId from 'transmute/uniqueId';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getInputWrapper = inputType => {
  switch (inputType) {
    case InputTypes.NoInputType:
      return FilterOperatorNoInputInput;
    case InputTypes.PropertyInputType:
      return FilterOperatorPropertyInput;
    case InputTypes.CtaInputType:
      return FilterOperatorCtaInput;
    case InputTypes.EmailLinkInputType:
      return FilterOperatorEmailLinkInput;
    case InputTypes.RangeInputType:
      return FilterOperatorHighValueInput;
    case InputTypes.RollingDateInputType:
      return FilterOperatorRollingDateInput;
    case InputTypes.RollingInequalityInputType:
      return FilterOperatorRollingInequalityInput;
    case InputTypes.RollingWithUnitInputType:
      return FilterOperatorRollingWithUnitInput;
    case InputTypes.RollingPropertyUpdatedInputType:
      return FilterOperatorRollingPropertyUpdated;
    case InputTypes.UrlInputType:
      return FilterOperatorUrlInput;
    default:
      return FilterOperatorValueInput;
  }
};
const shouldShowNullValueCheckbox = (filterFamily, operator) => {
  const hasValue = typeof operator.value !== 'undefined' || typeof operator.numberOfDays !== 'undefined';
  return hasValue && isOperatorIncludesObjectsWithNoValueSet(operator) || shouldOperatorAlwaysShowNoValueCheckbox(filterFamily, operator);
};
export default class FilterOperatorInput extends PureComponent {
  constructor(props) {
    super();
    this.handleOperatorChange = Operator => {
      const {
        initialValue,
        onChange,
        showIncludeUnknownValues,
        value,
        value: {
          field
        } = {},
        nullIssueRelatedState,
        updateNullIssueRelatedState
      } = this.props;
      let nextValue = value ? swapOperator(Operator, value, initialValue) : Operator.of(field);
      if (showIncludeUnknownValues && this.state.canUpdateNullIssueRelatedState) {
        const defaultNullValue = nullIssueRelatedState.defaultNullValueByOperator[this.getOperatorHashKey(nextValue)] || DefaultNullValueRecord();
        nextValue = nextValue.set('defaultNullValue', defaultNullValue);
        updateNullIssueRelatedState(this.getNullIssueRelatedStateUpdate(nextValue));
      }
      onChange(SyntheticEvent(nextValue));
    };
    this.handleChangeDefaultNullValue = defaultNullValue => {
      const {
        onChange,
        value,
        showIncludeUnknownValues,
        updateNullIssueRelatedState,
        nullIssueRelatedState
      } = this.props;
      if (showIncludeUnknownValues && this.state.canUpdateNullIssueRelatedState) {
        updateNullIssueRelatedState(Object.assign({}, nullIssueRelatedState, {
          defaultNullValueByOperator: Object.assign({}, nullIssueRelatedState.defaultNullValueByOperator, {
            [this.getOperatorHashKey(value)]: defaultNullValue
          })
        }));
      }
      const nextValue = value.set('defaultNullValue', defaultNullValue);
      onChange(SyntheticEvent(nextValue));
    };
    this.getIncludeObjectsWithNoAssociatedObjects = () => {
      const {
        filterBranch
      } = this.props;
      return getIn(['includeObjectsWithNoAssociatedObjects'], filterBranch);
    };
    this.getShowIncludeObjectsWithNoAssociatedObjects = () => {
      const {
        nullIssueRelatedState
      } = this.props;
      if (!this.state.canUpdateNullIssueRelatedState) {
        return false;
      }
      return getIn(['showIncludeObjectsWithNoAssociatedObjectsByFilterBranch', this.getBranchHashKey()], nullIssueRelatedState);
    };
    this.getShowIncludeObjectsWithNoValueSet = () => {
      const {
        value,
        nullIssueRelatedState
      } = this.props;
      if (!this.state.canUpdateNullIssueRelatedState) {
        return false;
      }
      const operatorHashKey = this.getOperatorHashKey(value);
      return getIn(['showIncludeObjectsWithNoValueSetByOperator', operatorHashKey], nullIssueRelatedState);
    };
    const {
      value: _value
    } = props;
    this.originalOperatorName = _value && _value.constructor;
    this.originalConstraintOperatorName = _value && _value.refinement && _value.refinement.constructor || undefined;
    this.renderRadioInput = this.renderRadioInput.bind(this);
    // In new code, please use ES6 spread args rather than partials
    // for easier debugging and readability:
    // (...args) => myFunction('partiallyAppliedParam', ...args)
    this.partial = memoize(partial);
    this.uniqueId = memoize(uniqueId);
    this.state = {
      canUpdateNullIssueRelatedState: false
    };
  }
  componentDidMount() {
    const {
      value,
      showIncludeUnknownValues,
      nullIssueRelatedState,
      updateNullIssueRelatedState
    } = this.props;
    const canUpdateNullIssueRelatedState = nullIssueRelatedState && typeof updateNullIssueRelatedState === 'function';
    this.setState({
      canUpdateNullIssueRelatedState
    });
    if (showIncludeUnknownValues && canUpdateNullIssueRelatedState) {
      updateNullIssueRelatedState(this.getNullIssueRelatedStateUpdate(value));
    }
  }
  getBranchHashKey() {
    const {
      nullIssueRelatedState
    } = this.props;
    return nullIssueRelatedState.conditionBranchPath.join('_');
  }
  getOperatorHashKey(operator) {
    const branchHashKey = this.getBranchHashKey();
    return `${branchHashKey}__${operator.field.name}__${operator.constructor}`;
  }
  getNullIssueRelatedStateUpdate(nextValue) {
    const {
      nullIssueRelatedState,
      filterBranch,
      filterFamily,
      getIsUngated,
      isNotUsingTimePoints
    } = this.props;
    const branchHashKey = this.getBranchHashKey();
    const operatorHashKey = this.getOperatorHashKey(nextValue);
    const newNullIssueRelatedState = Object.assign({}, nullIssueRelatedState);

    // update "null value" related state
    const cachedShowIncludeObjectsWithNoValueSet = nullIssueRelatedState.showIncludeObjectsWithNoValueSetByOperator[operatorHashKey];
    const newShouldShowNullValueCheckbox = shouldShowNullValueCheckbox(filterFamily, nextValue, getIsUngated, isNotUsingTimePoints);
    const isNullValueChangedFromFalseToTrue = cachedShowIncludeObjectsWithNoValueSet === false && newShouldShowNullValueCheckbox === true;
    if (cachedShowIncludeObjectsWithNoValueSet === undefined || isNullValueChangedFromFalseToTrue) {
      newNullIssueRelatedState.showIncludeObjectsWithNoValueSetByOperator = Object.assign({}, newNullIssueRelatedState.showIncludeObjectsWithNoValueSetByOperator, {
        [operatorHashKey]: shouldShowNullValueCheckbox(filterFamily, nextValue, getIsUngated, isNotUsingTimePoints)
      });
      newNullIssueRelatedState.defaultNullValueByOperator = Object.assign({}, newNullIssueRelatedState.defaultNullValueByOperator, {
        [operatorHashKey]: nextValue.defaultNullValue
      });
    }

    // update "null association" related state
    const cachedShowIncludeObjectsWithNoAssociatedObjects = nullIssueRelatedState.showIncludeObjectsWithNoAssociatedObjectsByFilterBranch[branchHashKey];
    if (cachedShowIncludeObjectsWithNoAssociatedObjects === undefined) {
      newNullIssueRelatedState.showIncludeObjectsWithNoAssociatedObjectsByFilterBranch = Object.assign({}, newNullIssueRelatedState.showIncludeObjectsWithNoAssociatedObjectsByFilterBranch, {
        [branchHashKey]: getIn(['includeObjectsWithNoAssociatedObjects'], filterBranch)
      });
    }
    return newNullIssueRelatedState;
  }
  getOperatorList() {
    const {
      getOperators,
      value: {
        field
      } = {}
    } = this.props;
    const definedOperators = getOperators(field);
    return definedOperators.union(OrderedSet.of(this.originalOperatorName));
  }
  renderRadioInput(opts) {
    const FAKE_VALUE = DATE;
    const {
        disabled,
        onEdit,
        onSelect,
        operator,
        text,
        value = FAKE_VALUE
      } = opts,
      rest = _objectWithoutPropertiesLoose(opts, _excluded);
    const {
      currencyCode,
      error,
      filterFamily,
      getDefaultTimezone,
      getFamilyValueResolver,
      getFilterFamilyObjectName,
      getObjectPropertyLabel,
      getInputComponent,
      getPlaceholder,
      getReferencedObjectType,
      getSpecialOptionsForReferenceType,
      isFiscalYearEnabled,
      isNotUsingTimePoints,
      isRollingDateOffsetInputEnabled,
      isXoEnabled,
      handleIncludeObjectsWithNoAssociatedObjectsChange,
      getIsUngated,
      isFilterIncompatible
    } = this.props;
    const isSelected = value && operator === value.constructor;
    const Input = isSelected && getInputComponent(value);
    const InputWrapper = isSelected && getInputWrapper(value.constructor.inputType);
    const showNoValueCheckbox = this.getShowIncludeObjectsWithNoValueSet();
    const showNoAssociationCheckbox = this.getShowIncludeObjectsWithNoAssociatedObjects();
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(UITooltip, {
        disabled: !disabled,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFilters.FilterOperatorInput.tooltipOperatorNotSupported"
        }),
        children: /*#__PURE__*/_jsx(UIRadioInput, Object.assign({}, rest, {
          checked: isSelected,
          className: "p-bottom-2",
          "data-selenium-test": `filter-operator-${operator.toString()}-radio`,
          disabled: disabled || isFilterIncompatible,
          name: this.uniqueId('operator-'),
          onChange: onSelect,
          children: text
        }))
      }), isSelected && InputWrapper && /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(InputWrapper, {
          InputComponent: Input,
          className: "m-bottom-2",
          condition: value,
          currencyCode: currencyCode,
          "data-selenium-test": `XOFilterEditor-operator-${operator.toString()}-input`,
          error: error,
          filterFamily: filterFamily,
          getDefaultTimezone: getDefaultTimezone,
          getIsUngated: getIsUngated,
          getReferencedObjectType: getReferencedObjectType,
          getSpecialOptionsForReferenceType: getSpecialOptionsForReferenceType,
          isFiscalYearEnabled: isFiscalYearEnabled,
          isNotUsingTimePoints: isNotUsingTimePoints,
          isRollingDateOffsetInputEnabled: isRollingDateOffsetInputEnabled,
          isXoEnabled: isXoEnabled,
          onChange: onEdit,
          placeholder: getPlaceholder(value),
          resolver: getFamilyValueResolver(value),
          value: value
        }), (showNoValueCheckbox || showNoAssociationCheckbox) && /*#__PURE__*/_jsxs(View, {
          as: "div",
          marginBlockEnd: "400",
          marginBlockStart: "100",
          marginInlineStart: "300",
          children: [showNoValueCheckbox && /*#__PURE__*/_jsx(FilterOperatorIncludeObjectsWithNoValueSetInput, {
            filterFamily: filterFamily,
            getObjectPropertyLabel: getObjectPropertyLabel,
            isUngated: getIsUngated,
            onChange: this.handleChangeDefaultNullValue,
            value: value
          }), showNoAssociationCheckbox && /*#__PURE__*/_jsx(FilterOperatorIncludeObjectsWithNoAssociatedObjectsInput, {
            className: showNoValueCheckbox ? 'm-top-2' : '',
            filterFamily: filterFamily,
            getFilterFamilyObjectName: getFilterFamilyObjectName,
            onChange: handleIncludeObjectsWithNoAssociatedObjectsChange,
            value: this.getIncludeObjectsWithNoAssociatedObjects()
          })]
        })]
      })]
    }, `${operator} ${getIn(['field', 'name'], value)}`);
  }
  render() {
    const {
      className,
      getOperatorLabel,
      onChange,
      value,
      value: {
        field
      } = {}
    } = this.props;
    const isFieldCalculatedProperty = [EQUATION, ROLLUP].includes(getPropertyType(field));
    const operatorsWithPropertyHistoryRequirement = [Operators.EverContainedAll, Operators.EverContainedAny, Operators.EverEqual, Operators.EverEqualAny, Operators.EverIn, Operators.NeverContained, Operators.NeverContainedAll, Operators.NeverEqual, Operators.NeverEqualAny, Operators.NeverIn, Operators.NotUpdatedInLastXDays, Operators.UpdatedAfter, Operators.UpdatedBefore, Operators.UpdatedInLastXDays];
    return /*#__PURE__*/_jsx(UIList, {
      className: className,
      children: this.getOperatorList().valueSeq().map(op => this.renderRadioInput({
        disabled: isFieldCalculatedProperty && operatorsWithPropertyHistoryRequirement.includes(op),
        onEdit: onChange,
        onSelect: this.partial(this.handleOperatorChange, op),
        operator: op,
        text: getOperatorLabel(op, field),
        value
      })).toJS()
    });
  }
}
FilterOperatorInput.defaultProps = {
  isXoEnabled: false
};
FilterOperatorInput.propTypes = {
  className: PropTypes.string,
  currencyCode: PropTypes.string.isRequired,
  error: FilterOperatorErrorType.isRequired,
  filterBranch: FilterType,
  filterFamily: PropTypes.string.isRequired,
  getDefaultTimezone: PropTypes.func.isRequired,
  getFamilyValueResolver: PropTypes.func.isRequired,
  getFilterFamilyObjectName: PropTypes.func.isRequired,
  getInputComponent: PropTypes.func.isRequired,
  getIsUngated: PropTypes.func.isRequired,
  getObjectPropertyLabel: PropTypes.func.isRequired,
  getOperatorLabel: PropTypes.func.isRequired,
  getOperators: PropTypes.func.isRequired,
  getPlaceholder: PropTypes.func.isRequired,
  getReferencedObjectType: PropTypes.func.isRequired,
  getSpecialOptionsForReferenceType: PropTypes.func.isRequired,
  handleIncludeObjectsWithNoAssociatedObjectsChange: PropTypes.func,
  initialValue: FilterOperatorType,
  isFilterIncompatible: PropTypes.bool.isRequired,
  isFiscalYearEnabled: PropTypes.bool.isRequired,
  isNotUsingTimePoints: PropTypes.bool,
  isRollingDateOffsetInputEnabled: PropTypes.bool,
  isXoEnabled: PropTypes.bool,
  nullIssueRelatedState: NullRelatedState,
  onChange: PropTypes.func.isRequired,
  showIncludeUnknownValues: PropTypes.bool.isRequired,
  updateNullIssueRelatedState: PropTypes.func,
  value: FilterOperatorType.isRequired
};